import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { SEOPageLayout, InlineCTA, FAQList } from "../../components/seo/SEOPageLayout";

const FAQS = [
  { q: "Who buys diabetic supplies near me in Jacksonville?", a: "Cash for Test Strips Jacksonville is a locally operated buyer serving Jacksonville and the surrounding Northeast Florida area. We purchase eligible unused, unexpired diabetic supplies from private sellers in person." },
  { q: "How do I know if my supplies are eligible?", a: "Send a photo of your boxes to (904) 955-9646 or use our contact form. We review the brand, expiration date, and packaging condition and let you know whether they qualify." },
  { q: "Is selling unused diabetic supplies legal?", a: "Yes, in most cases. Reselling unopened, unexpired diabetic test strips and supplies that were not paid for by Medicare or Medicaid is generally permitted. We do not accept boxes marked with those programs." },
  { q: "What areas do you serve?", a: "We serve Jacksonville and nearby cities including Jacksonville Beach, Orange Park, Green Cove Springs, St. Augustine, Fernandina Beach, Amelia Island, and Yulee, and much of Clay and Nassau counties." },
];

export default function WhoBuysNearMe() {
  return (
    <SEOPageLayout
      title="Who Buys Diabetic Supplies Near Me? | Jacksonville, FL"
      description="Looking for a place to sell eligible unused diabetic supplies near Jacksonville? Learn how Cash for Test Strips Jacksonville can help."
      canonical="https://cashforteststripsjacksonville.com/who-buys-diabetic-supplies-near-me/"
      breadcrumbLabel="Who Buys Diabetic Supplies Near Me"
      primaryKeyword="Local Buyer Near Jacksonville"
      h1="Who Buys Diabetic Supplies Near Me?"
      intro="If you have extra, unopened diabetic supplies and want a local buyer instead of a mail-in service, this page explains how our Jacksonville-based process works and how to find out if your supplies are eligible."
      faqs={FAQS}
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-local-buyer">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              Looking for a Local Buyer for Eligible Diabetic Supplies?
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              We are Cash for Test Strips Jacksonville — a locally operated business that purchases eligible unused diabetic test strips, CGM sensors, insulin pumps, and related supplies from people in the Jacksonville area. We meet in person, review supplies on the spot, and complete the transaction locally.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              You will not deal with a call center or a shipping label. You deal with the same person who reviews your supplies and completes the transaction.
            </p>
          </section>

          <section data-testid="section-check-eligibility">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How to Find Out If Your Supplies Are Eligible</h2>
            <ol className="space-y-3 text-lg text-[#4B5563] list-decimal list-inside">
              <li>Text or email a photo of the front of your box, showing the brand, product, and expiration date.</li>
              <li>We reply with whether that item currently qualifies and any additional details we need.</li>
              <li>If it qualifies, we schedule a public, in-person meetup at a location convenient for you.</li>
            </ol>
          </section>

          <section data-testid="section-accepted">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>What Types of Supplies Do We Accept?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              We accept certain brands of test strips (including Accu-Chek, OneTouch, Contour Next, ReliOn, True Metrix, and FreeStyle), continuous glucose monitors from Dexcom and FreeStyle Libre, insulin pump supplies from Omnipod, Medtronic, and Tandem, plus insulin and injection supplies. See the <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link> for the full breakdown.
            </p>
          </section>

          <section data-testid="section-why-local">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Why Work With a Jacksonville-Based Business?</h2>
            <ul className="space-y-2 text-lg text-[#4B5563] list-disc list-inside">
              <li><strong>Local service.</strong> In-person meetups at public locations near you.</li>
              <li><strong>Easy communication.</strong> Text or call one number and speak with a person, not a form.</li>
              <li><strong>Clear process.</strong> You know whether your supplies qualify before you leave the house.</li>
              <li><strong>Legitimate eligibility requirements.</strong> We tell you upfront what we can and cannot accept.</li>
            </ul>
          </section>

          <section data-testid="section-get-started">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>How to Get Started</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              The easiest way is to text a photo to (904) 955-9646 or use the <Link to="/sell-diabetic-test-strips/" className="text-[#0F5132] font-semibold underline">Sell Diabetic Test Strips</Link> page to submit your details. We usually respond quickly.
            </p>
            <div className="inline-flex items-center gap-2 text-[#0F5132] bg-[#0F5132]/10 px-4 py-2 rounded-full text-sm font-medium">
              <MapPin className="w-4 h-4" />
              Serving Jacksonville, FL and surrounding areas
            </div>
          </section>

          <section data-testid="section-faq">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>Frequently Asked Questions</h2>
            <FAQList faqs={FAQS} />
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Contact Us Today"
            sub="A quick photo is all it takes to find out if your supplies qualify."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
